// src/pages/ClientEdit.jsx
// V17 ENTERPRISE ULTRA-OPTIMIZED — useClientForm hook · ClientFormField · Carga dinámica
// =====================================================
// 🔥 V17: useClientForm hook unificado (nivel "edit")
// 🔥 V17: ClientFormField para renderizado declarativo
// 🔥 V17: CLIENT_FIELDS como config fuente única
// 🔥 V17: Carga dinámica de DataContext (code splitting)
// 🔥 V17: Sin constantes duplicadas
// 🔥 V17: Sin funciones de estilo duplicadas
// 🔥 V17: Sin Tooltip ni SkeletonLoader manuales
// 🔥 V17: ModalShell simplificado
// 🔥 V17: Atajos de teclado preservados
// 🔥 V17: Glassmorphism + micro-interacciones preservados
// 🔥 V17.1: ctxRef sincronizado con useClientForm
// 🔥 V17.1: handleSubmit protegido con new Event('submit')
// =====================================================
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Save, HelpCircle, X, AlertCircle, CheckCircle2, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useClientForm } from "../hooks/useClientForm";
import { CLIENT_FIELDS } from "../config/clientFormConfig";
import ClientFormField from "../components/forms/ClientFormField";

// ==========================================================
// 🧩 ModalShell simplificado (PRESERVADO)
// ==========================================================
const ModalShell = React.memo(({ title, tone = "primary", children, primaryColor = "#2563eb", isMobile }) => {
  const getToneColor = () => {
    if (tone === "warning") return "#f59e0b";
    if (tone === "danger") return "#e11d48";
    return primaryColor;
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl border border-color overflow-hidden"
      >
        <div 
          className={`${isMobile ? 'px-4 py-3' : 'px-5 py-3'} text-white font-semibold flex items-center gap-2`}
          style={{ backgroundColor: getToneColor() }}
        >
          {tone === "warning" && <AlertCircle className="w-4 h-4" />}
          {tone === "danger" && <ShieldCheck className="w-4 h-4" />}
          {tone === "primary" && <CheckCircle2 className="w-4 h-4" />}
          {title}
        </div>
        <div className={`${isMobile ? 'p-4' : 'p-5'} space-y-4`}>{children}</div>
      </motion.div>
    </div>
  );
});

ModalShell.displayName = 'ModalShell';

// ==========================================================
// 🧩 SkeletonLoader simplificado
// ==========================================================
const SkeletonLoader = React.memo(() => {
  const isMobile = useIsMobile();
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] overflow-auto">
      <div className={`min-h-full flex items-center justify-center ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className={`w-full max-w-5xl ${isMobile ? 'space-y-4' : 'space-y-8'}`}>
          <div className={`h-8 w-24 bg-card/20 rounded-lg animate-pulse ${isMobile ? 'h-6' : ''}`} />
          <div className="bg-card/95 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/30">
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-2 gap-6'}`}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="space-y-2">
                  <div className="h-3 w-16 bg-muted rounded" />
                  <div className={`h-10 w-full bg-muted rounded-xl animate-pulse ${isMobile ? 'h-9' : ''}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SkeletonLoader.displayName = 'SkeletonLoader';

// ==========================================================
// 🏆 COMPONENTE PRINCIPAL — V17.1 ULTRA-OPTIMIZED
// ==========================================================
export default function ClientEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast, addNotification, user } = useAuth();
  const isMobile = useIsMobile();

  const primaryColor = useMemo(() => {
    try {
      return getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || '#2563eb';
    } catch { return '#2563eb'; }
  }, []);

  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showTips, setShowTips] = useState(!isMobile);

  // 🔥 V17.1: DataContext ref para useClientForm
  const ctxRef = useRef(null);

  // ==========================================================
  // 🔥 V17.1: useClientForm hook unificado (con dataContextRef)
  // ==========================================================
  const {
    form,
    errors,
    touched,
    saving,
    canSubmit,
    dirty,
    displayValues,
    handleChange,
    handleSubmit,
    formConfig,
  } = useClientForm({
    level: "edit",
    initialData: clientData,
    dataContextRef: ctxRef,
    showToast,
    addNotification,
    onSuccess: (updatedClient) => {
      showToast('✅ Cliente atualizado com sucesso!', 'success');
      setTimeout(() => navigate(`/clientes/${updatedClient.id || id}`), 300);
    },
    onError: (error) => {
      showToast('Erro ao salvar. Os dados foram restaurados.', 'error');
    },
  });

  // ==========================================================
  // 🔥 V17.1: Cargar datos del cliente (con sincronización ctxRef)
  // ==========================================================
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const stored = localStorage.getItem('auth_user');
        const userId = stored ? JSON.parse(stored).id : user?.uid;
        if (!userId) return;
        
        const DataContext = (await import("../lib/data/context/DataContext")).default;
        const ctx = await DataContext.getInstance(userId);
        ctxRef.current = ctx;  // 🔥 V17.1: Sincronizar ctxRef para useClientForm
        const data = await ctx?.collections?.clients?.findById?.(id);
        
        if (!cancelled && data) {
          setClientData(data);
        } else if (!cancelled) {
          showToast('Cliente não encontrado', 'error');
          navigate("/clientes");
        }
      } catch (e) {
        if (!cancelled) showToast('Erro ao carregar cliente', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  // ==========================================================
  // 🔥 V17: Atajos de teclado
  // ==========================================================
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (dirty && canSubmit && !saving) setShowSaveModal(true);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (dirty) setShowExitModal(true);
        else navigate(`/clientes/${id}`);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dirty, canSubmit, saving, id]);

  const handleBack = useCallback(() => {
    if (dirty) setShowExitModal(true);
    else navigate(`/clientes/${id}`);
  }, [dirty, id]);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    navigate(`/clientes/${id}`);
  }, [id]);

  // ==========================================================
  // LOADING STATE
  // ==========================================================
  if (loading) return <SkeletonLoader />;
  
  if (!clientData) return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card p-8 rounded-2xl shadow-xl text-center max-w-sm mx-4"
      >
        <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-primary mb-2">Cliente inválido</h2>
        <p className="text-muted mb-4">Não foi possível carregar os dados do cliente.</p>
        <button
          onClick={() => navigate("/clientes")}
          className="px-5 py-2.5 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          style={{ backgroundColor: primaryColor }}
        >
          Voltar para lista
        </button>
      </motion.div>
    </div>
  );

  // ==========================================================
  // RENDER PRINCIPAL — V17 ULTRA MODERNO
  // ==========================================================
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-gradient-to-br from-slate-900/60 via-slate-800/50 to-slate-900/60 backdrop-blur-sm z-[9999] overflow-auto"
    >
      <div className={`min-h-full flex items-center justify-center ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className={`w-full max-w-5xl ${isMobile ? 'space-y-3' : 'space-y-6'}`}>
          
          {/* HEADER PREMIUM CON GLASSMORPHISM */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="flex items-center justify-between"
          >
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-all group"
              type="button"
            >
              <ArrowLeft className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} group-hover:-translate-x-1 transition-transform`} />
              <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>Voltar</span>
              {!isMobile && (
                <kbd className="ml-2 px-2 py-0.5 text-xs bg-card/20 rounded-md backdrop-blur-sm">ESC</kbd>
              )}
            </button>

            <div className="flex items-center gap-3">
              <AnimatePresence>
                {showTips && !isMobile && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-card/10 text-white rounded-lg text-sm backdrop-blur-sm border border-white/20"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span>⌘S para salvar • ESC para voltar</span>
                    <button
                      onClick={() => setShowTips(false)}
                      className="ml-2 p-0.5 hover:bg-card/20 rounded transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                whileHover={{ scale: isMobile ? 1 : 1.02 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setShowSaveModal(true)}
                type="button"
                disabled={saving || !dirty || !canSubmit}
                style={{ 
                  backgroundColor: dirty && canSubmit ? primaryColor : '#94a3b8',
                  boxShadow: dirty && canSubmit ? `0 8px 25px -5px ${primaryColor}80` : 'none'
                }}
                className={`text-white rounded-xl flex items-center gap-2 font-semibold transition-all disabled:cursor-not-allowed ${
                  isMobile ? 'px-4 py-2.5 text-sm' : 'px-5 py-2.5'
                } ${dirty && canSubmit ? 'hover:brightness-110 hover:shadow-lg' : ''}`}
              >
                {saving ? (
                  <>
                    <RefreshCw className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} animate-spin`} />
                    <span>{isMobile ? 'Salvando' : 'Salvando...'}</span>
                  </>
                ) : (
                  <>
                    <Save className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    <span>{isMobile ? 'Salvar' : 'Salvar Alterações'}</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>

          {/* CARD DE EDIÇÃO PREMIUM CON GLASSMORPHISM */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 30 }}
            className="relative bg-card/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 overflow-hidden"
          >
            {/* Barra de gradiente superior */}
            <div 
              className="absolute top-0 left-0 right-0 h-1.5"
              style={{ 
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}cc, ${primaryColor}88)` 
              }}
            />
            
            {/* Indicador de cambios */}
            <AnimatePresence>
              {dirty && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-700"
                >
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                  Alterações não salvas
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className={`${isMobile ? 'p-4 pt-6' : 'p-6 pt-7'}`}>
              
              {/* Título de la sección */}
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-xl flex items-center justify-center`}
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <AlertCircle className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: primaryColor }} />
                </div>
                <div>
                  <h2 className={`font-bold text-primary ${isMobile ? 'text-lg' : 'text-xl'}`}>
                    Editar Cliente
                  </h2>
                  <p className={`text-muted ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    {form.name || 'Cliente'}
                  </p>
                </div>
              </div>

              {/* 🔥 V17: Grid de campos con ClientFormField */}
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 gap-5'}`}>
                {Object.values(CLIENT_FIELDS)
                  .filter(f => ['name', 'email', 'phone', 'type', 'status', 'pipeline', 'temperature', 'source', 'budget', 'score'].includes(f.key))
                  .sort((a, b) => a.order - b.order)
                  .map(field => (
                    <div key={field.key} className={field.grid === 'full' ? 'md:col-span-2' : ''}>
                      <ClientFormField
                        fieldKey={field.key}
                        value={field.mask || field.type === 'tel' || field.type === 'currency' ? displayValues[field.key] : form[field.key]}
                        error={errors[field.key]}
                        touched={touched[field.key]}
                        onChange={handleChange}
                        disabled={saving}
                      />
                    </div>
                  ))}
              </div>

              {/* Resumo do cliente — Badges modernos */}
              {form.name && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className={`mt-6 pt-4 border-t border-color/60 ${isMobile ? 'mt-4 pt-3' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-muted`} />
                    <p className={`text-muted font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      Resumo do cliente
                    </p>
                    {dirty && (
                      <span className="flex items-center gap-1 text-amber-600 text-xs">
                        <Sparkles className="w-3 h-3" />
                        Modificado
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Type badge */}
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium bg-blue-50 text-blue-700 border-blue-200">
                      🏷️ {form.type || 'Comprador'}
                    </span>
                    {/* Pipeline badge */}
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium bg-purple-50 text-purple-700 border-purple-200">
                      📋 {form.pipeline || 'Lead Novo'}
                    </span>
                    {/* Temperature badge */}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium ${
                      form.temperature === 'Quente' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                      form.temperature === 'Morno' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                      <div className="w-2 h-2 rounded-full" style={{ 
                        backgroundColor: form.temperature === 'Quente' ? '#e11d48' : form.temperature === 'Morno' ? '#d97706' : '#2563eb'
                      }} />
                      {form.temperature || 'Morno'}
                    </span>
                    {/* Score badge */}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium ${
                      (form.score || 0) >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      (form.score || 0) >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-rose-50 text-rose-700 border-rose-200'
                    }`}>
                      <Sparkles className="w-3 h-3" />
                      Score: {form.score || 0}%
                    </span>
                    {/* Status badge */}
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium bg-muted text-secondary border-color">
                      <ShieldCheck className="w-3 h-3" />
                      {form.status || 'Ativo'}
                    </span>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Footer indicador */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center"
          >
            <p className={`text-white/60 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
              {dirty ? 'Você tem alterações não salvas' : 'Nenhuma alteração detectada'}
              {canSubmit ? ' • Pronto para salvar' : ' • Verifique os campos obrigatórios'}
            </p>
          </motion.div>
        </div>
      </div>

      {/* MODAL SALVAR */}
      <AnimatePresence>
        {showSaveModal && (
          <ModalShell title="Salvar alterações" tone="primary" primaryColor={primaryColor} isMobile={isMobile}>
            <p className={`text-secondary ${isMobile ? 'text-sm' : ''}`}>
              Confirma as alterações em <strong>{form.name}</strong>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowSaveModal(false)} disabled={saving} className="px-4 py-2.5 rounded-xl border border-color text-secondary hover:bg-muted font-medium">
                Cancelar
              </button>
              {/* 🔥 V17.1: handleSubmit protegido con new Event('submit') */}
              <button onClick={() => { setShowSaveModal(false); handleSubmit(new Event('submit')); }} disabled={saving} style={{ backgroundColor: primaryColor }} className="px-5 py-2.5 rounded-xl text-white hover:brightness-110 disabled:opacity-50 font-medium flex items-center gap-2">
                {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Salvando...</> : <><Save className="w-4 h-4" /> Confirmar</>}
              </button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      {/* MODAL SAIR */}
      <AnimatePresence>
        {showExitModal && (
          <ModalShell title="Sair sem salvar?" tone="warning" primaryColor={primaryColor} isMobile={isMobile}>
            <p className={`text-secondary ${isMobile ? 'text-sm' : ''}`}>
              Existem <strong>alterações não salvas</strong>. Deseja realmente sair?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowExitModal(false)} className="px-4 py-2.5 rounded-xl border border-color text-secondary hover:bg-muted font-medium">
                Continuar editando
              </button>
              <button onClick={confirmExit} className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium">
                Descartar
              </button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </motion.div>
  );
}